/**
 * Editor для doc.retail_returns (Порція Роздріб.5b).
 *
 * Особливості:
 *  - original_sale_id обов'язковий — посилання на оригінальний чек
 *  - items референсують original_sale_item_id (а не довільну номенклатуру)
 *  - backend автоматично відновлює бонуси і складські партії
 *  - 3 actions: Зберегти, Провести, Фіскалізувати + delete для чернеток
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { nomenclature as nomApi, orgs } from '@/api/catalogs';
import { retailSales } from '@/api/retailSales';
import {
  retailReturns,
  type ReturnItemCondition,
  type ReturnMethod,
} from '@/api/retailReturns';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DatePopover from '@/components/DatePopover';

interface ReturnItemRow {
  _key: string;
  line_number: number;
  original_sale_item_id: string;
  nomenclature_id: string;
  quantity: string;
  price_per_unit: string;
  item_condition: ReturnItemCondition;
}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  original_sale_id: string;
  pos_workstation_id: string;
  return_method: ReturnMethod;
  cash_amount: string;
  card_amount: string;
  bonus_restore_amount: string;
  return_reason: string;
  items: ReturnItemRow[];
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: '',
  original_sale_id: '',
  pos_workstation_id: '',
  return_method: 'cash',
  cash_amount: '0',
  card_amount: '0',
  bonus_restore_amount: '0',
  return_reason: '',
  items: [],
  comment: '',
};

export default function RetailReturnEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('retail.return.create');
  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 1000 }),
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  // Original sale lookup при isNew і вводі original_sale_id.
  const originalSale = useQuery({
    queryKey: ['retail-sale-for-return', form.original_sale_id],
    queryFn: () => retailSales.get(form.original_sale_id),
    enabled: isNew && !!form.original_sale_id,
  });

  const docQuery = useQuery({
    queryKey: ['retail-return', id],
    queryFn: () => retailReturns.get(id!),
    enabled: !isNew,
  });

  const numberPreview = useNumberPreview('doc.retail_returns', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        original_sale_id: d.original_sale_id,
        pos_workstation_id: d.pos_workstation_id,
        return_method: d.return_method,
        cash_amount: d.cash_amount,
        card_amount: d.card_amount,
        bonus_restore_amount: d.bonus_restore_amount,
        return_reason: d.return_reason ?? '',
        items: d.items.map((it) => ({
          _key: it.id,
          line_number: it.line_number,
          original_sale_item_id: it.original_sale_item_id,
          nomenclature_id: it.nomenclature_id,
          quantity: it.quantity,
          price_per_unit: it.price_per_unit,
          item_condition: (it.item_condition ?? 'normal') as ReturnItemCondition,
        })),
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  // При завантаженні оригіналу — pre-populate items (всі рядки оригіналу).
  useEffect(() => {
    if (isNew && originalSale.data && form.items.length === 0) {
      const orig = originalSale.data;
      setForm((f) => ({
        ...f,
        pos_workstation_id: orig.pos_workstation_id,
        items: orig.items.map((it) => ({
          _key: it.id,
          line_number: it.line_number,
          original_sale_item_id: it.id,
          nomenclature_id: it.nomenclature_id,
          quantity: it.quantity,
          price_per_unit: it.price_per_unit,
          item_condition: 'normal',
        })),
      }));
    }
  }, [isNew, originalSale.data, form.items.length]);

  const posted = !!docQuery.data?.posted;
  const fiscalStatus = docQuery.data?.fiscal_status;
  const status = docQuery.data?.status;

  const totalReturn = useMemo(
    () =>
      form.items.reduce(
        (s, it) => s + Number(it.quantity || 0) * Number(it.price_per_unit || 0),
        0,
      ),
    [form.items],
  );

  const updateItem = (key: string, patch: Partial<ReturnItemRow>) => {
    setForm({
      ...form,
      items: form.items.map((i) => (i._key === key ? { ...i, ...patch } : i)),
    });
  };

  const removeItem = (key: string) => {
    setForm({ ...form, items: form.items.filter((i) => i._key !== key) });
  };

  const payload = (): Parameters<typeof retailReturns.create>[0] => ({
    organization_id: form.organization_id,
    document_number: form.document_number || null,
    document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
    original_sale_id: form.original_sale_id,
    pos_workstation_id: form.pos_workstation_id,
    return_method: form.return_method,
    cash_amount: form.cash_amount,
    card_amount: form.card_amount,
    bonus_restore_amount: form.bonus_restore_amount,
    return_reason: form.return_reason || null,
    items: form.items.map(({ _key: _drop, ...rest }) => rest),
    comment: form.comment || null,
  });

  const create = useMutation({
    mutationFn: () => retailReturns.create(payload()),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-returns'] });
      navigate(`/retail/retail-returns/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      retailReturns.update(id!, {
        document_number: form.document_number || null,
        document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
        return_reason: form.return_reason || null,
        comment: form.comment || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-returns'] });
      qc.invalidateQueries({ queryKey: ['retail-return', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  // Новий: створити та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await retailReturns.create(payload());
      await retailReturns.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-returns'] });
      navigate(`/retail/retail-returns/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  // Збережений: зберегти правки та провести одним кліком (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await retailReturns.update(id!, {
        document_number: form.document_number || null,
        document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
        return_reason: form.return_reason || null,
        comment: form.comment || null,
      });
      await retailReturns.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-returns'] });
      qc.invalidateQueries({ queryKey: ['retail-return', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const unpost = useMutation({
    mutationFn: () => retailReturns.unpost(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['retail-return', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  const fiscalize = useMutation({
    mutationFn: () => retailReturns.fiscalize(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['retail-return', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailReturns.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-returns'] });
      navigate('/retail/retail-returns');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const canUnpost = posted && fiscalStatus !== 'fiscalized' && fiscalStatus !== 'pending';

  function validate(): boolean {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        original_sale_id: form.original_sale_id,
        pos_workstation_id: form.pos_workstation_id,
      },
      {
        organization_id: 'Юрособа',
        original_sale_id: 'Оригінальний чек',
        pos_workstation_id: 'POS-станція',
      },
    );
    if (err) { setError(err); return false; }
    if (form.items.length === 0) {
      setError('Додайте позиції перед проведенням');
      return false;
    }
    return true;
  }

  const handleSave = () => {
    if (!validate()) return;
    if (isNew) create.mutate();
    else update.mutate();
  };
  // Новий: зберегти та провести.
  const handleSaveAndPost = () => {
    if (!validate()) return;
    createAndPost.mutate();
  };
  // Збережений: зберегти правки та провести.
  const handleUpdateAndPost = () => {
    if (!validate()) return;
    updateAndPost.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? `Нове повернення ${numberPreview ? `(${numberPreview})` : ''}`
            : `Повернення ${form.document_number}`}
          {posted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ Проведено
            </span>
          )}
          {fiscalStatus === 'fiscalized' && (
            <span style={{ marginLeft: '0.5rem', color: '#1d4ed8', fontSize: '1rem' }}>
              📋 Фіскалізовано
            </span>
          )}
          {status === 'voided' && (
            <span style={{ marginLeft: '0.5rem', color: '#6b7280', fontSize: '1rem' }}>
              ⊘ Анульовано
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <DocumentToolbar
            onBackToList={() => navigate('/retail/retail-returns')}
            onSave={handleSave}
            saveDisabled={
              create.isPending || update.isPending || createAndPost.isPending || form.items.length === 0
            }
            savePending={create.isPending || update.isPending}
            isPosted={posted}
            isNew={isNew}
            onPost={isNew ? handleSaveAndPost : handleUpdateAndPost}
            postDisabled={
              create.isPending || createAndPost.isPending || updateAndPost.isPending
              || update.isPending || form.items.length === 0
            }
            postPending={createAndPost.isPending || updateAndPost.isPending}
            postTitle="Зберігає поточні зміни та проводить документ"
            postable={isNew || !posted || canUnpost}
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => {
              if (window.confirm('Видалити чернетку?')) remove.mutate();
            }}
            deletePending={remove.isPending}
          />
          {!isNew && posted && fiscalStatus !== 'fiscalized' && (
            <RequirePermission code="retail.return.fiscalize">
              <button onClick={() => fiscalize.mutate()} disabled={fiscalize.isPending}>
                Фіскалізувати
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.code} — {o.name}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field">
          <label>Дата документу</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={posted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>ID оригінального чека</label>
          <input
            value={form.original_sale_id}
            onChange={(e) => setForm({ ...form, original_sale_id: e.target.value })}
            placeholder="UUID чека"
            disabled={!isNew}
          />
          {originalSale.data && (
            <small style={{ color: '#15803d', display: 'block', marginTop: '0.25rem' }}>
              ✓ Чек {originalSale.data.document_number}, сума:{' '}
              {Number(originalSale.data.total_amount).toFixed(2)} грн
            </small>
          )}
        </div>
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>Позиції повернення</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #cbd5e1', background: '#f1f5f9' }}>
            <th style={{ padding: '0.5rem', width: 50, textAlign: 'left' }}>#</th>
            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Номенклатура</th>
            <th style={{ padding: '0.5rem', width: 110, textAlign: 'right' }}>К-сть</th>
            <th style={{ padding: '0.5rem', width: 120, textAlign: 'right' }}>Ціна</th>
            <th style={{ padding: '0.5rem', width: 140, textAlign: 'left' }}>Стан</th>
            <th style={{ padding: '0.5rem', width: 120, textAlign: 'right' }}>Сума</th>
            <th style={{ padding: '0.5rem', width: 50 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it) => {
            const rowSum = Number(it.quantity || 0) * Number(it.price_per_unit || 0);
            return (
              <tr key={it._key} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
                <td style={{ padding: '0.5rem' }}>
                  {nomQ.data?.items.find((n) => n.id === it.nomenclature_id)?.name ??
                    it.nomenclature_id}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.001"
                    value={it.quantity}
                    onChange={(e) => updateItem(it._key, { quantity: e.target.value })}
                    disabled={posted}
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{it.price_per_unit}</td>
                <td style={{ padding: '0.5rem' }}>
                  <select
                    value={it.item_condition}
                    onChange={(e) =>
                      updateItem(it._key, { item_condition: e.target.value as ReturnItemCondition })
                    }
                    disabled={posted}
                  >
                    <option value="normal">Нормальний</option>
                    <option value="damaged">Пошкоджений</option>
                    <option value="expired">Прострочений</option>
                  </select>
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{rowSum.toFixed(2)}</td>
                <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                  {!posted && (
                    <button onClick={() => removeItem(it._key)} title="Видалити з повернення">
                      ×
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div
        style={{
          marginTop: '1rem',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.75rem',
        }}
      >
        <div>
          <h3>Спосіб повернення</h3>
          <div className="field">
            <label>Метод</label>
            <select
              value={form.return_method}
              onChange={(e) =>
                setForm({ ...form, return_method: e.target.value as ReturnMethod })
              }
              disabled={posted}
            >
              <option value="cash">Готівка</option>
              <option value="card">На картку</option>
              <option value="bonus">Бонусами</option>
              <option value="mixed">Змішана</option>
            </select>
          </div>
          <div className="field">
            <label>Готівкою</label>
            <input
              type="number"
              step="0.01"
              value={form.cash_amount}
              onChange={(e) => setForm({ ...form, cash_amount: e.target.value })}
              disabled={posted}
            />
          </div>
          <div className="field">
            <label>На картку</label>
            <input
              type="number"
              step="0.01"
              value={form.card_amount}
              onChange={(e) => setForm({ ...form, card_amount: e.target.value })}
              disabled={posted}
            />
          </div>
          <div className="field">
            <label>Відновлено бонусів</label>
            <input
              type="number"
              step="0.01"
              value={form.bonus_restore_amount}
              onChange={(e) => setForm({ ...form, bonus_restore_amount: e.target.value })}
              disabled={posted}
            />
          </div>
        </div>
        <div>
          <h3>Підсумки</h3>
          <div style={{ fontSize: '1.25rem', marginTop: '0.5rem' }}>
            <strong>До повернення: {totalReturn.toFixed(2)} грн</strong>
          </div>
          <div className="field" style={{ marginTop: '1rem' }}>
            <label>Причина повернення</label>
            <textarea
              value={form.return_reason}
              onChange={(e) => setForm({ ...form, return_reason: e.target.value })}
              rows={3}
              disabled={posted}
            />
          </div>
        </div>
      </div>

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <textarea
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          rows={2}
          disabled={posted}
        />
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>
          {error}
        </div>
      )}
    </div>
  );
}
